// What the app may tell Google Analytics, and nothing else (#458, #557).
//
// Firebase Analytics in the web app, loaded unless the visitor declines in the
// cookie banner (#755), recording page views and a short list of product
// moments, which both phones send too, so one funnel covers all three
// platforms (#557). src/lib/analytics.js is the web's runtime — when it loads,
// and how a Decline turns it off; this file is the adapter over the list.
//
// The banner's one answer also covers Google Ads conversion measurement
// (#754), but the Ads tag is a separate runtime (src/lib/adConversions.js)
// sending one event of its own, and no event on this list is given to it. Its
// thank-you pages, `/welcome` and `/subscribed`, are screens on the core's
// list like any other.
//
// The list is `core/src/analytics.rs` (#557): which events exist, which
// platforms send each, every parameter each may carry and its kind, the
// screens by pattern and the route matching. The web, Android and iOS ask it
// before anything reaches an SDK. test/core/analytics.wasm.test.js replays
// `core/src/analytics_vectors.json` (its `recorded` cases) through this
// adapter. The core's header says why each moment is on the list.
//
// ── No personal data, by construction ──────────────────────────────────────
//
// A parameter is one of three kinds and nothing else: a value from a fixed
// list (an enum), a whole-number count, or a yes/no. There is no free-text
// kind, so a name, an email, a note, an amount or an id has no way in — a
// caller that passes one has it dropped, and a value outside its list drops
// the parameter rather than being cleaned. The pages are the routes' own
// patterns (`/students/:id`), never the URL in the address bar, which carries a
// student's id on one screen and could carry anything in a query string. No
// user id is ever set: Google's own random browser id is the only identifier,
// and a Decline deletes it (analytics.js).
//
// "First" is judged from what this browser holds when the write is made — no
// students (or calendar entries) loaded, and the list finished loading — as
// the getting-started checklist judges it. A studio that deletes everything
// and starts again is counted twice.
//
// What stays here is the browser's own: where a visitor came from, and GA's
// cookies to remove on a Decline. Neither is a question a phone asks.

import { coreSync } from './core.js';
import { cookieDeletions, gaCookieNames, referrerOrigin } from '../../marketing/adTag.js';

/** The event names, for callers. `test/domain/analyticsEvents.test.js` pins them to the core's list. */
export const ANALYTICS = Object.freeze({
  pageView: 'page_view',
  signUp: 'sign_up',
  firstStudentAdded: 'first_student_added',
  firstEventScheduled: 'first_event_scheduled',
  attendanceMarked: 'attendance_marked',
  invoiceCreated: 'invoice_created',
  reportRun: 'report_run',
  accountDeleted: 'account_deleted',
});

/** This platform, as the core's list names it. */
export const PLATFORM = 'web';

/**
 * The core's whole list: `{ events, screens, platforms, otherScreen,
 * countCeiling }`, each event with the platforms that send it and its
 * parameters' kinds and values.
 */
export function analyticsPolicy() {
  return coreSync().analyticsPolicy();
}

/** The routes in src/App.jsx, as patterns — the core's screens the web has. */
export function pagePaths() {
  return analyticsPolicy().screens.filter((s) => s.platforms.includes(PLATFORM)).map((s) => s.pattern);
}

/** A loose JavaScript value as the core's parameter, or nothing. */
function toParam(key, value) {
  if (typeof value === 'string') return { key, text: value };
  if (typeof value === 'number') return { key, number: value };
  if (typeof value === 'boolean') return { key, flag: value };
  return null;
}

/** The core's cleaned parameters back as a plain object. */
function fromParams(params) {
  const out = {};
  for (const p of params) out[p.key] = p.text ?? p.number;
  return out;
}

const answer = (c) => (c.ok ? { ok: true, name: c.name, params: fromParams(c.params) } : { ok: false, reason: c.reason });

/**
 * The event as the web may send it, or why not. A name off the list (or one
 * only the phones send) is refused; a parameter off its event's list, or with
 * a value its kind does not allow, is dropped and the rest is sent.
 * @returns {{ ok: true, name: string, params: object } | { ok: false, reason: string }}
 */
export function cleanAnalyticsEvent(name, params) {
  const given = params && typeof params === 'object' && !Array.isArray(params) ? params : {};
  const list = Object.keys(given).map((k) => toParam(k, given[k])).filter(Boolean);
  return answer(coreSync().analyticsClean(String(name ?? ''), PLATFORM, list));
}

/**
 * The route pattern a pathname is reported as. `pathname` is the router's,
 * without the app's base path; a trailing slash is ignored.
 */
export function pageViewPath(pathname) {
  return coreSync().analyticsPagePath(String(pathname ?? ''));
}

/**
 * A page view's parameters: the pattern as the path and the title (the app's
 * one static title says nothing per screen), and a location that is the
 * origin and the pattern, so that no event — gtag.js stamps the location on
 * every one — carries the real address.
 */
export function pageViewParams(pathname, origin) {
  return fromParams(coreSync().analyticsPageView(String(pathname ?? ''), String(origin ?? '')).params);
}

/**
 * Where the visitor came from, as an origin only. The path and query of a
 * referring page can carry anything — a reset link's code, a checkout
 * session — and only the site is a useful answer to "where from". Written
 * once, in marketing/adTag.js, because the Ads tag on the marketing site
 * follows the same rule.
 */
export { referrerOrigin };

// ── Withdrawing: the identifiers to remove ──────────────────────────────────

// gtag.js's cookies: `_ga` (the random browser id) and `_ga_<stream>` (the
// session), and the two older ones a GA4 tag no longer sets but a browser may
// still hold. The names are marketing/adTag.js `gaCookieNames`, because the
// marketing site removes them too, on a Decline made there.
export { gaCookieNames };

/**
 * Every `document.cookie` assignment that removes the GA cookies present.
 *
 * gtag.js's default `cookie_domain: 'auto'` writes on the widest domain the
 * browser accepts — `.<brand domain>` from `app.<brand domain>`, the project's
 * own `.web.app` subdomain on a preview — so a cookie is expired on the host
 * and on every parent domain of it (marketing/adTag.js `cookieDeletions`,
 * which the Ads cookies go through too).
 */
export function gaCookieDeletions(cookieHeader, hostname) {
  return cookieDeletions(gaCookieNames(cookieHeader), hostname);
}
